/**
 * PeerReviewCycles — HR cycle management page.
 *
 * Allows HR / Admin to create, edit, activate, and close peer review cycles.
 * Shows summary stats cards + sortable / paginated table.
 * Navigate buttons go to the template manager and assignment manager for each cycle.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PeopleIcon from "@mui/icons-material/People";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import PageTitle from "../../../components/Page Component/PageTitle";
import { deleteData, fetchData, postData, putData } from "../../../store/actions";
import { RootState } from "../../../store/Store";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import { StandardTablePagination } from "components/Pagination/StandardTablePagination";
import { PAGINATION_CONFIG } from "utils/paginationConfig";
import {
  peerReviewCyclesUrl,
  peerReviewCycleDetailUrl,
  activatePeerReviewCycleUrl,
  closePeerReviewCycleUrl,
  peerReviewTemplatesUrl,
  viewActiveUsers,
} from "../../../Service/Apis";
import { PeerReviewCycle, PeerReviewCycleFormData, PeerReviewCycleStatus } from "./Types/PeerReviewTypes";

interface EmployeeOption {
  id: string;
  label: string;
  email: string;
}

const defaultForm: PeerReviewCycleFormData = {
  name: "",
  description: "",
  start_date: "",
  end_date: "",
  max_reviewers_per_employee: 9999,
};

const statusColor = (status: PeerReviewCycleStatus): "default" | "info" | "success" | "error" => {
  if (status === "active") return "success";
  if (status === "closed") return "error";
  return "default";
};

const PeerReviewCycles: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const selectedRole = useSelector((state: RootState) => state.auth.selectedRole);

  useEffect(() => {
    if (selectedRole !== "HR" && selectedRole !== "Admin") {
      navigate("/pms/peer-review/my-reviews", { replace: true });
    }
  }, [selectedRole, navigate]);

  const [cycles, setCycles] = useState<PeerReviewCycle[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [stats, setStats] = useState({ total: 0, active: 0, draft: 0, closed: 0 });

  // Create / Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCycle, setEditingCycle] = useState<PeerReviewCycle | null>(null);
  const [form, setForm] = useState<PeerReviewCycleFormData>(defaultForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string>("");
  const dialogContentRef = useRef<HTMLDivElement>(null);

  // Always scrolls to top and sets the error — works even when the message is unchanged
  const showFormError = (msg: string) => {
    setFormError(msg);
    dialogContentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Templates for dropdown
  const [allTemplates, setAllTemplates] = useState<Array<{ id: string; name: string; active_cycle_count: number; assigned_cycle_count: number; assigned_cycles_info: Array<{ id: string; name: string; status: string }> }>>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");

  // Participants
  const [allEmployees, setAllEmployees] = useState<EmployeeOption[]>([]);
  const [selectedParticipants, setSelectedParticipants] = useState<EmployeeOption[]>([]);
  const [participantSearch, setParticipantSearch] = useState("");

  // Allowed Reviewees
  const [selectedAllowedReviewees, setSelectedAllowedReviewees] = useState<EmployeeOption[]>([]);
  const [allowedRevieweeSearch, setAllowedRevieweeSearch] = useState("");

  // People dialog
  const [peopleDialogCycle, setPeopleDialogCycle] = useState<PeerReviewCycle | null>(null);

  // Action confirm dialogs
  const [activateTarget, setActivateTarget] = useState<PeerReviewCycle | null>(null);
  const [closeTarget, setCloseTarget] = useState<PeerReviewCycle | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PeerReviewCycle | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  /** ID of cycle currently being activated (emails in-flight); shows spinner in Status cell */
  const [activatingCycleId, setActivatingCycleId] = useState<string | null>(null);

  const buildUrl = () => {
    const params = new URLSearchParams();
    params.set("page", String(page + 1));
    params.set("page_size", String(rowsPerPage));
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (searchQuery) params.set("search", searchQuery);
    if (sortField) params.set("ordering", sortOrder === "desc" ? `-${sortField}` : sortField);
    return `${peerReviewCyclesUrl}?${params.toString()}`;
  };

  const fetchCycles = () => {
    setLoading(true);
    dispatch(fetchData(buildUrl()))
      .then((res: any) => {
        if (res) {
          setCycles(res.results ?? res);
          setTotalCount(res.count ?? (res.results ?? res).length);
          if (res.stats) setStats(res.stats);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCycles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, statusFilter, sortField, sortOrder]);

  // Load employees once on mount so the people-dialog can resolve names immediately
  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(0);
      fetchCycles();
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setPage(0);
  };

  const fetchTemplates = () => {
    dispatch(fetchData(`${peerReviewTemplatesUrl}?page_size=100`)).then((res: any) => {
      if (res) setAllTemplates(res.results ?? res);
    });
  };

  const fetchEmployees = () => {
    dispatch(fetchData(`${viewActiveUsers}?page_size=500`)).then((res: any) => {
      const list = res?.results ?? res ?? [];
      setAllEmployees(
        list
          .map((e: any) => ({
            id: String(e.id),
            label: `${e.first_name || ""} ${e.last_name || ""}`.trim() || e.email,
            email: e.email,
          }))
          .sort((a: EmployeeOption, b: EmployeeOption) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
          )
      );
    });
  };

  const openCreateDialog = () => {
    setEditingCycle(null);
    setForm(defaultForm);
    setSelectedTemplateId("");
    setSelectedParticipants([]);
    setParticipantSearch("");
    setSelectedAllowedReviewees([]);
    setAllowedRevieweeSearch("");
    setFormError("");
    fetchTemplates();
    fetchEmployees();
    setDialogOpen(true);
  };

  const openEditDialog = (cycle: PeerReviewCycle) => {
    setEditingCycle(cycle);
    setForm({
      name: cycle.name,
      description: cycle.description,
      start_date: cycle.start_date ?? "",
      end_date: cycle.end_date ?? "",
      max_reviewers_per_employee: 9999,
    });
    setSelectedTemplateId(cycle.template_id ?? "");
    setParticipantSearch("");
    setAllowedRevieweeSearch("");
    setFormError("");
    fetchTemplates();
    fetchEmployees();
    // Pre-load existing participants from cycle data
    const existing: EmployeeOption[] = (cycle.participants ?? []).map((id: string) => ({
      id,
      label: id, // will be replaced once allEmployees loads
      email: "",
    }));
    setSelectedParticipants(existing);
    // Pre-load existing allowed_reviewees from cycle data
    const existingAllowedReviewees: EmployeeOption[] = (cycle.allowed_reviewees ?? []).map((id: string) => ({
      id,
      label: id,
      email: "",
    }));
    setSelectedAllowedReviewees(existingAllowedReviewees);
    setDialogOpen(true);
  };

  // Once employees load, resolve any participant stubs that only have an id
  const resolvedParticipants: EmployeeOption[] = useMemo(() => {
    if (allEmployees.length === 0) return selectedParticipants;
    return selectedParticipants.map((p) => {
      if (p.label !== p.id) return p; // already resolved
      const match = allEmployees.find((e) => e.id === p.id);
      return match ?? p;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEmployees, selectedParticipants]);

  const resolvedAllowedReviewees: EmployeeOption[] = useMemo(() => {
    if (allEmployees.length === 0) return selectedAllowedReviewees;
    return selectedAllowedReviewees.map((p) => {
      if (p.label !== p.id) return p;
      const match = allEmployees.find((e) => e.id === p.id);
      return match ?? p;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEmployees, selectedAllowedReviewees]);

  // Pre-compute O(1) lookup sets so the list items don't do O(n) .some() per employee
  const selectedParticipantIdSet = useMemo(
    () => new Set(resolvedParticipants.map((p) => p.id)),
    [resolvedParticipants]
  );
  const selectedRevieweeIdSet = useMemo(
    () => new Set(resolvedAllowedReviewees.map((p) => p.id)),
    [resolvedAllowedReviewees]
  );

  // Memoized participant list items — only recomputes when the employee list,
  // search query, or selection changes. Typing in other form fields (name,
  // description, dates) does NOT trigger this, eliminating the input lag.
  const participantListItems = useMemo(() => {
    const q = participantSearch.toLowerCase();
    const filtered = allEmployees.filter(
      (e) => !q || e.label.toLowerCase().includes(q) || e.email.toLowerCase().includes(q)
    );
    if (filtered.length === 0) {
      return (
        <Typography variant="body2" color="text.disabled" sx={{ p: 2, textAlign: "center" }}>
          No employees match "{participantSearch}"
        </Typography>
      );
    }
    return filtered.map((employee) => {
      const isSelected = selectedParticipantIdSet.has(employee.id);
      return (
        <FormControlLabel
          key={employee.id}
          control={
            <Checkbox
              size="small"
              checked={isSelected}
              onChange={() => {
                setSelectedParticipants((prev) =>
                  isSelected ? prev.filter((p) => p.id !== employee.id) : [...prev, employee]
                );
              }}
              sx={{ py: 0.25, pl: 1.5 }}
            />
          }
          label={
            <Box>
              <Typography variant="body2" lineHeight={1.3}>{employee.label}</Typography>
              <Typography variant="caption" color="text.secondary" lineHeight={1.2}>{employee.email}</Typography>
            </Box>
          }
          sx={{
            display: "flex", alignItems: "center", m: 0, px: 0.5, py: 0.5, width: "100%",
            "&:hover": { bgcolor: "action.hover" },
            borderBottom: "1px solid", borderColor: "divider", "&:last-child": { borderBottom: 0 },
          }}
        />
      );
    });
  }, [allEmployees, participantSearch, selectedParticipantIdSet]);

  // Memoized allowed-reviewee list items — same pattern
  const revieweeListItems = useMemo(() => {
    const q = allowedRevieweeSearch.toLowerCase();
    const filtered = allEmployees.filter(
      (e) => !q || e.label.toLowerCase().includes(q) || e.email.toLowerCase().includes(q)
    );
    if (filtered.length === 0) {
      return (
        <Typography variant="body2" color="text.disabled" sx={{ p: 2, textAlign: "center" }}>
          No employees match "{allowedRevieweeSearch}"
        </Typography>
      );
    }
    return filtered.map((employee) => {
      const isSelected = selectedRevieweeIdSet.has(employee.id);
      return (
        <FormControlLabel
          key={employee.id}
          control={
            <Checkbox
              size="small"
              checked={isSelected}
              onChange={() => {
                setSelectedAllowedReviewees((prev) =>
                  isSelected ? prev.filter((p) => p.id !== employee.id) : [...prev, employee]
                );
              }}
              sx={{ py: 0.25, pl: 1.5 }}
            />
          }
          label={
            <Box>
              <Typography variant="body2" lineHeight={1.3}>{employee.label}</Typography>
              <Typography variant="caption" color="text.secondary" lineHeight={1.2}>{employee.email}</Typography>
            </Box>
          }
          sx={{
            display: "flex", alignItems: "center", m: 0, px: 0.5, py: 0.5, width: "100%",
            "&:hover": { bgcolor: "action.hover" },
            borderBottom: "1px solid", borderColor: "divider", "&:last-child": { borderBottom: 0 },
          }}
        />
      );
    });
  }, [allEmployees, allowedRevieweeSearch, selectedRevieweeIdSet]);

  const handleSave = async () => {
    if (!form.name) {
      showFormError("Cycle name is required.");
      return;
    }

    // Self-review dead-end guard: if both lists are set, every participant must have
    // at least one allowed reviewee who is not themselves.
    if (resolvedParticipants.length > 0 && resolvedAllowedReviewees.length > 0) {
      const revieweeIds = new Set(resolvedAllowedReviewees.map((r) => r.id));
      const deadEnds = resolvedParticipants.filter(
        (p) => revieweeIds.size === 1 && revieweeIds.has(p.id)
      );
      if (deadEnds.length > 0) {
        const names = deadEnds.map((p) => p.label || p.email).join(", ");
        showFormError(
          `${names} would have nobody to review — they are the only allowed reviewee. ` +
          `Add at least one more allowed reviewee, or remove them from participants.`
        );
        return;
      }
    }

    setSaving(true);
    setFormError("");

    // Template assignment is now part of the cycle payload — one atomic API call
    const payload = {
      ...form,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      participants: resolvedParticipants.map((p) => p.id),
      allowed_reviewees: resolvedAllowedReviewees.map((p) => p.id),
      set_template_id: selectedTemplateId || null,
    };

    try {
      if (editingCycle) {
        await dispatch(putData(peerReviewCycleDetailUrl(editingCycle.id), payload));
        showToast("Cycle updated.", "success", false);
      } else {
        await dispatch(postData(peerReviewCyclesUrl, payload));
        showToast("Cycle created.", "success", false);
      }
      setDialogOpen(false);
      fetchCycles();
    } catch (err: any) {
      // Surface the specific backend validation message if available
      const data = err?.response?.data;
      const detail =
        data?.allowed_reviewees?.[0] ||
        data?.set_template_id?.[0] ||
        data?.non_field_errors?.[0] ||
        data?.detail;
      showFormError(detail ?? "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async () => {
    if (!activateTarget) return;
    const targetId = activateTarget.id;
    setActionLoading(true);

    let dialogClosed = false;

    // After 10 s: close the confirmation dialog and move the spinner into the Status cell
    const timer = setTimeout(() => {
      dialogClosed = true;
      setActivateTarget(null);
      setActionLoading(false);
      setActivatingCycleId(targetId);
    }, 10_000);

    try {
      await dispatch(postData(activatePeerReviewCycleUrl(targetId), {}));
      showToast("Cycle activated. Emails sent to all reviewers.", "success", false);
    } catch {
      showToast("Failed to activate cycle.", "error", false);
    } finally {
      clearTimeout(timer);
      setActivatingCycleId(null);
      setActionLoading(false);
      if (!dialogClosed) setActivateTarget(null);
      fetchCycles();
    }
  };

  const handleClose = async () => {
    if (!closeTarget) return;
    setActionLoading(true);
    try {
      await dispatch(postData(closePeerReviewCycleUrl(closeTarget.id), {}));
      showToast("Cycle closed.", "success", false);
      setCloseTarget(null);
      fetchCycles();
    } catch {
      showToast("Failed to close cycle.", "error", false);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      await dispatch(deleteData(peerReviewCycleDetailUrl(deleteTarget.id)));
      showToast("Cycle deleted.", "success", false);
      setDeleteTarget(null);
      fetchCycles();
    } catch {
      showToast("Failed to delete cycle.", "error", false);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 10, mb: 6 }}>
      <PageTitle>Peer Review Cycles</PageTitle>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: "Total", value: stats.total, color: "#1976d2" },
          { label: "Active", value: stats.active, color: "#2e7d32" },
          { label: "Draft", value: stats.draft, color: "#ed6c02" },
          { label: "Closed", value: stats.closed, color: "#d32f2f" },
        ].map((s) => (
          <Grid item xs={6} sm={3} key={s.label}>
            <Card elevation={2}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" fontWeight="bold" color={s.color}>
                  {s.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {s.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters + Create */}
      <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2}>
        <TextField
          size="small"
          label="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>
        <Box flexGrow={1} />
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={openCreateDialog}
        >
          New Cycle
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                background: "linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)",
                "& .MuiTableCell-head": { color: "white", fontWeight: 700, fontSize: "0.82rem", py: 1.5 },
                "& .MuiTableSortLabel-root": { color: "rgba(255,255,255,0.85)" },
                "& .MuiTableSortLabel-root:hover": { color: "white" },
                "& .MuiTableSortLabel-root.Mui-active": { color: "white" },
                "& .MuiTableSortLabel-icon": { color: "white !important" },
              }}
            >
              <TableCell>
                <TableSortLabel
                  active={sortField === "name"}
                  direction={sortField === "name" ? sortOrder : "asc"}
                  onClick={() => handleSort("name")}
                >
                  Cycle Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Template</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === "start_date"}
                  direction={sortField === "start_date" ? sortOrder : "asc"}
                  onClick={() => handleSort("start_date")}
                >
                  Start Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === "end_date"}
                  direction={sortField === "end_date" ? sortOrder : "asc"}
                  onClick={() => handleSort("end_date")}
                >
                  End Date
                </TableSortLabel>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : cycles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">No cycles found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              cycles.map((cycle, idx) => (
                <TableRow
                  key={cycle.id}
                  hover
                  sx={{
                    bgcolor: idx % 2 === 0 ? "white" : "grey.50",
                    borderLeft: 4,
                    borderColor:
                      cycle.status === "active"
                        ? "success.main"
                        : cycle.status === "closed"
                        ? "error.light"
                        : "grey.300",
                    transition: "background 0.15s",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <TableCell sx={{ py: 1.5 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {cycle.name}
                    </Typography>
                    {cycle.description && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {cycle.description.length > 60
                          ? cycle.description.slice(0, 60) + "…"
                          : cycle.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    {cycle.template_name ? (
                      <Chip
                        label={cycle.template_name}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ maxWidth: 200, "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }}
                      />
                    ) : (
                      <Typography variant="caption" color="error.main" fontStyle="italic">
                        No template
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    {cycle.start_date ? (
                      <Typography variant="body2">{cycle.start_date}</Typography>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    {cycle.end_date ? (
                      <Typography variant="body2">{cycle.end_date}</Typography>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    {activatingCycleId === cycle.id ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={16} color="success" thickness={5} />
                        <Typography variant="caption" color="text.secondary" fontStyle="italic">
                          Activating…
                        </Typography>
                      </Box>
                    ) : (
                      <Chip
                        label={cycle.status.charAt(0).toUpperCase() + cycle.status.slice(1)}
                        color={statusColor(cycle.status)}
                        size="small"
                        sx={{ fontWeight: 600, minWidth: 64 }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1.5, whiteSpace: "nowrap" }}>
                    <Tooltip title="View participants & reviewees">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => setPeopleDialogCycle(cycle)}
                        sx={{ mr: 1 }}
                      >
                        <PeopleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {/* Active toggle — draft→active or active→closed */}
                    <Tooltip
                      title={
                        cycle.status === "closed"
                          ? "Cycle is closed"
                          : cycle.status === "active"
                          ? "Disable cycle"
                          : "Enable cycle"
                      }
                    >
                      <span>
                        <Switch
                          size="small"
                          checked={cycle.status === "active"}
                          disabled={cycle.status === "closed"}
                          color="success"
                          onChange={() => {
                            if (cycle.status === "draft") setActivateTarget(cycle);
                            else if (cycle.status === "active") setCloseTarget(cycle);
                          }}
                        />
                      </span>
                    </Tooltip>
                    {cycle.status === "draft" && (
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEditDialog(cycle)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {cycle.status === "draft" && (
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteTarget(cycle)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <StandardTablePagination
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />

      {/* Participants & Allowed Reviewees Dialog */}
      {peopleDialogCycle && (() => {
        const cycle = peopleDialogCycle;
        const participantIds = cycle.participants ?? [];
        const allowedRevieweeIds = cycle.allowed_reviewees ?? [];

        const participantNames = participantIds.length > 0
          ? participantIds.map((id) => {
              const emp = allEmployees.find((e) => e.id === id);
              return emp ? emp.label : id;
            })
          : [];

        const revieweeNames = allowedRevieweeIds.length > 0
          ? allowedRevieweeIds.map((id) => {
              const emp = allEmployees.find((e) => e.id === id);
              return emp ? emp.label : id;
            })
          : [];

        return (
          <Dialog
            open
            onClose={() => setPeopleDialogCycle(null)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ pr: 6 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <PeopleIcon color="primary" fontSize="small" />
                <Typography variant="h6" fontWeight={700}>
                  {cycle.name}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Participants &amp; Allowed Reviewees
              </Typography>
              <IconButton
                size="small"
                onClick={() => setPeopleDialogCycle(null)}
                sx={{ position: "absolute", top: 12, right: 12 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers>
              {/* Participants */}
              <Box mb={2}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Participants
                  </Typography>
                  <Chip
                    label={participantNames.length === 0 ? "All employees" : `${participantNames.length}`}
                    size="small"
                    color={participantNames.length === 0 ? "default" : "primary"}
                    variant="outlined"
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                </Box>
                {participantNames.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    No restriction — all active employees can participate.
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {participantNames.map((name, i) => (
                      <ListItem
                        key={i}
                        disableGutters
                        sx={{
                          py: 0.4,
                          px: 1,
                          borderRadius: 1,
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            bgcolor: "primary.main",
                            mr: 1.5,
                            flexShrink: 0,
                          }}
                        />
                        <ListItemText
                          primary={name}
                          primaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>

              <Divider />

              {/* Allowed Reviewees */}
              <Box mt={2}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Allowed Reviewees
                  </Typography>
                  <Chip
                    label={revieweeNames.length === 0 ? "All participants" : `${revieweeNames.length}`}
                    size="small"
                    color={revieweeNames.length === 0 ? "default" : "secondary"}
                    variant="outlined"
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                </Box>
                {revieweeNames.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    No restriction — any participant can be reviewed.
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {revieweeNames.map((name, i) => (
                      <ListItem
                        key={i}
                        disableGutters
                        sx={{
                          py: 0.4,
                          px: 1,
                          borderRadius: 1,
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            bgcolor: "secondary.main",
                            mr: 1.5,
                            flexShrink: 0,
                          }}
                        />
                        <ListItemText
                          primary={name}
                          primaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setPeopleDialogCycle(null)}>Close</Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* Create / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setTimeout(() => (document.activeElement as HTMLElement)?.blur(), 0); }}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { minHeight: isMobile ? undefined : "60vh" } }}
      >
        <DialogTitle>{editingCycle ? "Edit Cycle" : "Create Peer Review Cycle"}</DialogTitle>
        <DialogContent dividers ref={dialogContentRef}>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <TextField
            label="Cycle Name"
            fullWidth
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <Box display="flex" gap={2} mb={2} flexDirection={{ xs: "column", sm: "row" }}>
            <TextField
              label="Start Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              helperText="Optional — defaults to activation date"
            />
            <TextField
              label="End Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              helperText="Optional — defaults to 1 month after start"
            />
          </Box>

          {/* Template selection — only changeable in draft status */}
          <FormControl fullWidth size="small" sx={{ mt: 1 }}
            disabled={editingCycle !== null && editingCycle.status !== "draft"}
          >
            <InputLabel>Template (optional)</InputLabel>
            <Select
              value={selectedTemplateId}
              label="Template (optional)"
              onChange={(e) => setSelectedTemplateId(e.target.value)}
            >
              <MenuItem value="">
                <em>No template</em>
              </MenuItem>
              {allTemplates
                .map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                    {t.active_cycle_count > 0 && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        ml={1}
                      >
                        · in use by {t.active_cycle_count} active cycle{t.active_cycle_count !== 1 ? "s" : ""}
                      </Typography>
                    )}
                  </MenuItem>
                ))}
            </Select>
            {editingCycle && editingCycle.status !== "draft" && (
              <Typography variant="caption" color="text.secondary" mt={0.5}>
                Template can only be changed while the cycle is in Draft status.
              </Typography>
            )}
          </FormControl>

          {/* Participants + Allowed Reviewees side by side on md+, stacked on mobile */}
          <Box display="flex" gap={2} sx={{ mt: 2 }} alignItems="flex-start" flexDirection={{ xs: "column", md: "row" }}>

          {/* Participants inline checklist */}
          <Box sx={{ flex: 1, minWidth: 0, width: { xs: "100%", md: "auto" } }}>
            {/* Label + description — fixed height, no action buttons here */}
            <Typography variant="body2" color="text.primary" fontWeight={500}>
              Reviewers
              <Typography component="span" variant="caption" color="text.primary" ml={0.75}>
                (leave empty to notify all employees)
              </Typography>
            </Typography>
            <Typography variant="caption" color="text.primary" display="block" mt={0.25} mb={0.75}>
              Or Select employees to give a review in this particular cycle.
            </Typography>

            {/* Actions row — always rendered so both columns stay in sync */}
            <Box display="flex" gap={1} alignItems="center" minHeight={28} mb={0.5}>
              <Button
                size="small"
                variant="text"
                sx={{ textTransform: "none", fontSize: "0.75rem", p: 0, minWidth: 0 }}
                onClick={() => setSelectedParticipants(allEmployees)}
                disabled={resolvedParticipants.length === allEmployees.length}
              >
                Select all ({allEmployees.length})
              </Button>
              {resolvedParticipants.length > 0 && (
                <>
                  <Typography variant="caption" color="text.disabled">·</Typography>
                  <Button
                    size="small"
                    variant="text"
                    sx={{ textTransform: "none", fontSize: "0.75rem", p: 0, minWidth: 0, color: "error.main" }}
                    onClick={() => setSelectedParticipants([])}
                  >
                    Clear
                  </Button>
                  <Typography variant="caption" color="text.disabled">·</Typography>
                  <Chip
                    label={`${resolvedParticipants.length} selected`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                </>
              )}
            </Box>

            <TextField
              size="small"
              fullWidth
              placeholder="Search employees…"
              value={participantSearch}
              onChange={(e) => setParticipantSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 0.5 }}
            />

            <Paper variant="outlined" sx={{ maxHeight: 220, overflowY: "auto", borderRadius: 1 }}>
              {participantListItems}
            </Paper>

            {resolvedParticipants.length > 0 && (
              <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                {resolvedParticipants.length} selected — invitation emails will be sent on save
              </Typography>
            )}
          </Box>

          {/* Allowed Reviewees inline checklist */}
          <Box sx={{ flex: 1, minWidth: 0, width: { xs: "100%", md: "auto" } }}>
            {/* Label + description — fixed height, no action buttons here */}
            <Typography variant="body2" color="text.primary" fontWeight={500}>
              Allowed Reviewees
              <Typography component="span" variant="caption" color="text.primary" ml={0.75}>
                (leave empty to allow all participants to be reviewed)
              </Typography>
            </Typography>
            <Typography variant="caption" color="text.primary" display="block" mt={0.25} mb={0.75}>
              Or Select employees who will be reviewed by the selected reviewers.
            </Typography>

            {/* Actions row — always rendered so both columns stay in sync */}
            <Box display="flex" gap={1} alignItems="center" minHeight={28} mb={0.5}>
              <Button
                size="small"
                variant="text"
                sx={{ textTransform: "none", fontSize: "0.75rem", p: 0, minWidth: 0 }}
                onClick={() => setSelectedAllowedReviewees(allEmployees)}
                disabled={resolvedAllowedReviewees.length === allEmployees.length}
              >
                Select all ({allEmployees.length})
              </Button>
              {resolvedAllowedReviewees.length > 0 && (
                <>
                  <Typography variant="caption" color="text.disabled">·</Typography>
                  <Button
                    size="small"
                    variant="text"
                    sx={{ textTransform: "none", fontSize: "0.75rem", p: 0, minWidth: 0, color: "error.main" }}
                    onClick={() => setSelectedAllowedReviewees([])}
                  >
                    Clear
                  </Button>
                  <Typography variant="caption" color="text.disabled">·</Typography>
                  <Chip
                    label={`${resolvedAllowedReviewees.length} selected`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                </>
              )}
            </Box>

            <TextField
              size="small"
              fullWidth
              placeholder="Search employees…"
              value={allowedRevieweeSearch}
              onChange={(e) => setAllowedRevieweeSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 0.5 }}
            />

            <Paper variant="outlined" sx={{ maxHeight: 220, overflowY: "auto", borderRadius: 1 }}>
              {revieweeListItems}
            </Paper>

            {resolvedAllowedReviewees.length > 0 && (
              <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                Only these {resolvedAllowedReviewees.length} employee(s) can be reviewed in this cycle.
              </Typography>
            )}
          </Box>

          </Box>{/* end side-by-side flex */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setTimeout(() => (document.activeElement as HTMLElement)?.blur(), 0); }} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editingCycle ? "Save Changes" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activate Confirm */}
      <Dialog open={!!activateTarget} onClose={() => setActivateTarget(null)}>
        <DialogTitle>Enable Cycle</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            Enable <strong>{activateTarget?.name}</strong>? Reviewers will be notified and
            submissions will open.
          </Typography>

          {/* Block activation if no template is assigned */}
          {activateTarget && !(activateTarget as any).template_id && (
            <Alert severity="error" sx={{ mb: 2 }}>
              No template is assigned to this cycle. Go to{" "}
              <strong>Peer Review → Templates</strong> to create a template, then edit this
              cycle to assign it before activating.
            </Alert>
          )}

          {/* Show projected dates only when they were not set at creation */}
          {activateTarget && (activateTarget as any).template_id &&
            (!activateTarget.start_date || !activateTarget.end_date) && (() => {
            const today = new Date();
            const calcStart = activateTarget.start_date
              ? activateTarget.start_date
              : today.toISOString().slice(0, 10);
            const endDate = new Date(calcStart);
            endDate.setMonth(endDate.getMonth() + 1);
            const calcEnd = activateTarget.end_date
              ? activateTarget.end_date
              : endDate.toISOString().slice(0, 10);
            return (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight="medium" mb={0.5}>
                  No dates were provided — the following will be auto-assigned:
                </Typography>
                <Typography variant="body2">
                  Start Date: <strong>{calcStart}</strong>
                </Typography>
                <Typography variant="body2">
                  End Date: <strong>{calcEnd}</strong>
                </Typography>
              </Alert>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateTarget(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleActivate}
            disabled={actionLoading || !(activateTarget as any)?.template_id}
          >
            {actionLoading ? <CircularProgress size={20} /> : "Enable"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close Confirm */}
      <Dialog open={!!closeTarget} onClose={() => setCloseTarget(null)}>
        <DialogTitle>Disable Cycle</DialogTitle>
        <DialogContent>
          <Typography>
            Disable <strong>{closeTarget?.name}</strong>? No further submissions will be accepted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseTarget(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleClose} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : "Disable"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Cycle</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PeerReviewCycles;
